import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { quizQuestions, quizResults, calculateResult } from "@/mocks/quiz";

type Screen = "intro" | "quiz" | "result";

export default function QuizEmbed() {
  const [screen, setScreen] = useState<Screen>("intro");
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [resultKey, setResultKey] = useState<string>("");
  const [leadData, setLeadData] = useState({ firstName: "", lastName: "", email: "" });
  const [leadValid, setLeadValid] = useState(false);

  const startQuiz = useCallback(() => {
    setScreen("quiz");
    setCurrentQ(0);
    setAnswers([]);
    setSelectedOption(null);
  }, []);

  const selectOption = useCallback((qIndex: number, optIndex: number) => {
    setSelectedOption(optIndex);
    const newAnswers = [...answers];
    newAnswers[qIndex] = optIndex;
    setAnswers(newAnswers);
  }, [answers]);

  const nextQ = useCallback(() => {
    if (selectedOption === null) return;
    if (currentQ < quizQuestions.length - 1) {
      setCurrentQ(currentQ + 1);
      setSelectedOption(answers[currentQ + 1] ?? null);
    }
  }, [currentQ, selectedOption, answers]);

  const prevQ = useCallback(() => {
    if (currentQ > 0) {
      setCurrentQ(currentQ - 1);
      setSelectedOption(answers[currentQ - 1] ?? null);
    }
  }, [currentQ, answers]);

  const calculateAndShowResult = useCallback(() => {
    const result = calculateResult(answers);
    setResultKey(result);
    setScreen("result");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [answers]);

  const restartQuiz = useCallback(() => {
    setScreen("intro");
    setCurrentQ(0);
    setAnswers([]);
    setSelectedOption(null);
    setLeadData({ firstName: "", lastName: "", email: "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const { firstName, lastName, email } = leadData;
    setLeadValid(
      firstName.trim().length > 0 &&
      lastName.trim().length > 0 &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    );
  }, [leadData]);

  const isLeadStep = currentQ === quizQuestions.length - 1;
  const progress = ((currentQ + 1) / quizQuestions.length) * 100;
  const question = quizQuestions[currentQ];

  return (
    <div>
      {/* Intro Screen */}
      {screen === "intro" && (
        <div className="max-w-xl mx-auto bg-background-50 rounded-lg border border-background-200/70 p-8 md:p-10 text-center">
          <div className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-primary-100 text-primary-700 mb-4">
            Free 2-Minute Quiz
          </div>
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 leading-tight mb-3">
            Which country is right for your{" "}
            <span className="text-primary-600">dental treatment?</span>
          </h2>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
            Answer 6 quick questions about your treatment goals, budget, and travel preferences — and we&apos;ll match you to the country most likely to suit your needs.
          </p>
          <div className="flex items-center justify-center gap-3 md:gap-4 mb-8">
            {["🇭🇺", "🇹🇷", "🇵🇱", "🇪🇸"].map((flag, i) => (
              <div key={i} className="text-center">
                <div className="text-2xl md:text-3xl mb-1">{flag}</div>
                <div className="text-xs text-foreground-500">
                  {["Hungary", "Turkey", "Poland", "Spain"][i]}
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={startQuiz}
            className="inline-flex items-center justify-center px-6 py-3 text-base font-medium rounded-md bg-primary-500 text-white hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Start the Quiz
            <span className="w-5 h-5 flex items-center justify-center ml-2">
              <i className="ri-arrow-right-line"></i>
            </span>
          </button>
          <p className="text-xs text-foreground-500 mt-4">Takes under 2 minutes</p>
        </div>
      )}

      {/* Quiz Screen */}
      {screen === "quiz" && question && (
        <div className="max-w-2xl mx-auto">
          {/* Progress bar */}
          <div className="mb-6">
            <div className="h-1.5 bg-background-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-500 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Step indicator */}
          <div className="flex items-center justify-center gap-1 mb-8">
            {quizQuestions.map((_, i) => (
              <div key={i} className="flex items-center">
                <div
                  className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-semibold transition-colors ${
                    i === currentQ
                      ? "bg-primary-500 text-white"
                      : i < currentQ
                      ? "bg-primary-100 text-primary-700"
                      : "bg-background-200 text-foreground-400"
                  }`}
                >
                  {i < currentQ ? (
                    <i className="ri-check-line"></i>
                  ) : (
                    i + 1
                  )}
                </div>
                {i < quizQuestions.length - 1 && (
                  <div
                    className={`w-4 h-px mx-0.5 ${
                      i < currentQ ? "bg-primary-400" : "bg-background-300"
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Question Card */}
          <div className="bg-background-50 rounded-lg border border-background-200/70 p-5 md:p-7">
            {!isLeadStep ? (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs font-medium text-primary-600">
                    Q{currentQ + 1} of {quizQuestions.length}
                  </span>
                  <span className="text-xs text-foreground-400">
                    {question.category}
                  </span>
                </div>
                <h2 className="text-base md:text-lg font-semibold text-foreground-900 mb-5">
                  {question.title}
                </h2>
                <div className="space-y-2.5 mb-6">
                  {question.options.map((opt, i) => (
                    <button
                      key={i}
                      onClick={() => selectOption(currentQ, i)}
                      className={`w-full flex items-center gap-3 p-3.5 rounded-lg border text-left transition-colors cursor-pointer ${
                        selectedOption === i
                          ? "border-primary-400 bg-primary-50/70"
                          : "border-background-200/70 bg-background-50 hover:bg-background-100/70"
                      }`}
                    >
                      <span
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                          selectedOption === i
                            ? "border-primary-500 bg-primary-500"
                            : "border-background-300"
                        }`}
                      >
                        {selectedOption === i && (
                          <span className="w-2 h-2 rounded-full bg-white" />
                        )}
                      </span>
                      <span className="text-lg">{opt.icon}</span>
                      <span className="text-sm text-foreground-700">
                        {opt.label}
                      </span>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs font-medium text-primary-600">
                    Q{currentQ + 1} of {quizQuestions.length}
                  </span>
                  <span className="text-xs text-foreground-400">
                    Almost There!
                  </span>
                </div>
                <h2 className="text-base md:text-lg font-semibold text-foreground-900 mb-3">
                  Where should we send your personalised result?
                </h2>
                <p className="text-sm text-foreground-600 mb-5">
                  Enter your details below to unlock your free destination match and checklist.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-xs font-medium text-foreground-700 mb-1">
                      First name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={leadData.firstName}
                      onChange={(e) =>
                        setLeadData({ ...leadData, firstName: e.target.value })
                      }
                      placeholder="Jane"
                      autoComplete="given-name"
                      className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-700 mb-1">
                      Last name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={leadData.lastName}
                      onChange={(e) =>
                        setLeadData({ ...leadData, lastName: e.target.value })
                      }
                      placeholder="Smith"
                      autoComplete="family-name"
                      className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="mb-5">
                  <label className="block text-xs font-medium text-foreground-700 mb-1">
                    Email address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={leadData.email}
                    onChange={(e) =>
                      setLeadData({ ...leadData, email: e.target.value })
                    }
                    placeholder="jane@example.com"
                    autoComplete="email"
                    className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  />
                  <p className="text-xs text-foreground-500 mt-1.5 flex items-center gap-1">
                    <span className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-lock-line text-xs"></i>
                    </span>
                    We respect your privacy. No spam, ever. Unsubscribe at any time.
                  </p>
                </div>
                <p className="text-xs text-foreground-500 mb-5">
                  By clicking below you agree to receive your quiz result and occasional helpful emails from Dental Safety Abroad. We will never share your details with third parties.
                </p>
              </>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <button
                onClick={prevQ}
                disabled={currentQ === 0}
                className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md border transition-colors whitespace-nowrap ${
                  currentQ === 0
                    ? "border-background-200 text-foreground-300 cursor-not-allowed"
                    : "border-background-300 text-foreground-600 hover:bg-background-100 cursor-pointer"
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center mr-1">
                  <i className="ri-arrow-left-line"></i>
                </span>
                Back
              </button>
              <button
                onClick={isLeadStep ? calculateAndShowResult : nextQ}
                disabled={isLeadStep ? !leadValid : selectedOption === null}
                className={`inline-flex items-center px-5 py-2.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                  (isLeadStep ? leadValid : selectedOption !== null)
                    ? "bg-primary-500 text-white hover:bg-primary-600 cursor-pointer"
                    : "bg-background-300 text-foreground-400 cursor-not-allowed"
                }`}
              >
                {isLeadStep ? "See My Result" : "Next"}
                <span className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-right-line"></i>
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Result Screen */}
      {screen === "result" && resultKey && quizResults[resultKey] && (
        <div className="max-w-2xl mx-auto">
          {(() => {
            const result = quizResults[resultKey];
            return (
              <div className="bg-background-50 rounded-lg border border-background-200/70 overflow-hidden">
                {/* Match score bar */}
                <div className="bg-[#182940] p-5 md:p-7 text-center">
                  <div className="text-xs text-white/50 mb-1">Match score</div>
                  <div className="text-4xl md:text-5xl font-bold text-white mb-1">
                    {result.matchScore}
                    <span className="text-lg md:text-xl font-normal text-white/60">%</span>
                  </div>
                  <div className="text-xs text-white/50 mb-4">
                    based on your {quizQuestions.length} answers
                  </div>
                  <div className="max-w-xs mx-auto">
                    <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary-400 rounded-full transition-all duration-700"
                        style={{ width: `${result.matchScore}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Reasons */}
                <div className="flex flex-wrap items-center justify-center gap-2 p-4 md:p-5 border-b border-background-200/70">
                  {result.reasons.map((r, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-100 text-xs text-foreground-700"
                    >
                      <span>{r.icon}</span>
                      {r.label}
                    </span>
                  ))}
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-px bg-background-200/70">
                  {result.stats.map((s, i) => (
                    <div
                      key={i}
                      className="bg-background-50 p-4 md:p-5 text-center"
                    >
                      <div className="text-xl md:text-2xl mb-1">{s.icon}</div>
                      <div className="text-lg md:text-xl font-bold text-foreground-900">
                        {s.value}
                      </div>
                      <div className="text-xs text-foreground-500">{s.label}</div>
                    </div>
                  ))}
                </div>

                {/* Result header */}
                <div className="p-5 md:p-7 text-center border-b border-background-200/70">
                  <div className="text-3xl md:text-4xl mb-2">{result.flag}</div>
                  <div className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-primary-100 text-primary-700 mb-3">
                    Your Personalised Result
                  </div>
                  <h2 className="text-lg md:text-xl font-bold text-foreground-950 leading-tight">
                    {result.title}
                  </h2>
                </div>

                {/* Result body */}
                <div className="p-5 md:p-7">
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-5">
                    {result.intro}
                  </p>

                  <div className="bg-amber-50/70 border border-amber-200/60 rounded-lg p-4 md:p-5 mb-5">
                    <h4 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-1.5">
                      <span className="w-4 h-4 flex items-center justify-center text-amber-600">
                        <i className="ri-alert-line"></i>
                      </span>
                      Things to be aware of
                    </h4>
                    <ul className="space-y-1.5">
                      {result.awareOf.map((item, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-2 text-sm text-foreground-700"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mb-5">
                    <p className="text-sm font-medium text-foreground-900 mb-2">
                      Get the Dental Abroad Safety System to see:
                    </p>
                    <ul className="space-y-1.5 mb-5">
                      {result.ctaBullets.map((item, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-2 text-sm text-foreground-600"
                        >
                          <span className="w-4 h-4 flex items-center justify-center text-primary-500 mt-0.5 shrink-0">
                            <i className="ri-check-line"></i>
                          </span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="space-y-3">
                    <a
                      href="https://www.dentalsafetyabroad.com/protection-packages/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full text-center px-5 py-3 text-sm font-medium rounded-md bg-primary-500 text-white hover:bg-primary-600 transition-colors whitespace-nowrap"
                    >
                      <span className="mr-1">🛡️</span> View your Protection System Options
                    </a>
                    <a
                      href="https://dentalsafetyabroad.gumroad.com/l/uiskv"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full text-center px-5 py-3 text-sm font-medium rounded-md border border-background-300 text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
                    >
                      <span className="mr-1">📋</span> Download the Red Flags Checklist
                    </a>
                  </div>

                  <div className="mt-5 pt-5 border-t border-background-200/70 text-center">
                    <button
                      onClick={restartQuiz}
                      className="text-sm text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer inline-flex items-center gap-1"
                    >
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-restart-line"></i>
                      </span>
                      Retake the quiz
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Disclaimer */}
          <p className="text-xs text-foreground-500 text-center leading-relaxed mt-6">
            <strong>Important:</strong> This quiz is for general information purposes only. It is not a substitute for professional dental advice. Results are based on pattern-matching your stated preferences — not a clinical assessment. Always consult a qualified dentist before making any treatment decision. Dental Safety Abroad is an independent information resource and is not affiliated with any clinic. See our full{" "}
            <Link to="/legal" className="text-primary-600 hover:underline">
              disclaimer
            </Link>
            .
          </p>
        </div>
      )}
    </div>
  );
}